<template>
    <!-- 不能编辑 -->
    <div class="stand-pagination-table-container">
        <vxe-table
            ref="vxeTable"
            :data="data"
            :loading="loading"
            :size="size"
            :border="true"
            :stripe="true"
            :height="computedTableHeight"
            :virtual-x-config="{ enabled: true, gt: 20 }"
            :virtual-y-config="{ enabled: true, gt: 25 }"
            :edit-config="{ trigger: 'click', mode: 'cell', showStatus: true }"
            keep-source
            @checkbox-change="handleCheckboxChange"
            @checkbox-all="handleCheckboxAllChange"
            @cell-dblclick="handleCellDblClick"
        >
            <template v-if="child">
                <vxe-colgroup
                    v-for="(item, key) in processedColumns"
                    :key="item.title"
                    :title="item.title"
                    :width="item.width || 100"
                    :fixed="item.fixed"
                    :align="item.align || 'center'"
                >
                    <vxe-column
                        v-if="child"
                        v-for="column in item.child"
                        :key="column.field"
                        :field="column.field"
                        :title="column.title"
                        :width="column.width"
                        :edit-render="column.editRender"
                        :formatter="column.vxeFormatter"
                        :show-overflow="
                            column.showOverflow === false ? false : 'tooltip'
                        "
                        :fixed="column.fixed"
                    ></vxe-column>
                </vxe-colgroup>
            </template>
            <template v-else>
                <vxe-column
                    v-for="column in processedColumns"
                    :key="column.field"
                    :field="column.field"
                    :title="column.title"
                    :width="column.width"
                    :edit-render="column.editRender"
                    :formatter="column.vxeFormatter"
                    :show-overflow="
                        column.showOverflow === false ? false : 'tooltip'
                    "
                    :fixed="column.fixed"
                ></vxe-column>
            </template>
        </vxe-table>

        <!-- 分页器 -->
        <vxe-pager
            v-if="showPage && paginationConfig.total > 0"
            :current-page="paginationConfig.currentPage"
            :page-size="paginationConfig.pageSize"
            :total="paginationConfig.total"
            :page-sizes="pageSizeOptions"
            :layouts="[
                'PrevJump',
                'PrevPage',
                'JumpNumber',
                'NextPage',
                'NextJump',
                'Sizes',
                'FullJump',
                'Total',
            ]"
            @page-change="handlePageChange"
        ></vxe-pager>
    </div>
</template>

<script>
import { number2ThousandsFormat } from "@/libs/logic.js";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200];

export default {
    name: "OptimizedVxeTable", // 使用更明确的组件名
    props: {
        columns: { type: Array, required: true },
        data: { type: Array, required: true },
        // 加载状态
        loading: { type: Boolean, default: false },
        // 是否为子表格
        child: { type: Boolean, default: false },
        // 表格尺寸
        size: { type: String, default: "small" },
        // 外部高度调整值（用于动态计算高度）
        extHeight: { type: Number, default: 0 },
        // 是否自动高度（设置为 true 时，extHeight 无效，高度由 VxeTable 自适应）
        autoHeight: { type: Boolean, default: false },
        // 分页配置对象，推荐使用 .sync 修饰符或由父组件显式处理事件更新
        // 示例: { currentPage: 1, pageSize: 20, total: 0 }
        pagination: {
            type: Object,
            default: () => ({ currentPage: 1, pageSize: 20, total: 0 })
        },
        // 可选的分页大小选项
        pageSizeOptions: {
            type: Array,
            default: () => DEFAULT_PAGE_SIZE_OPTIONS
        },
        showPage: { type: Boolean, default: true }
    },
    data() {
        return {
            tableHeight: 400,
            selectedRows: []
        };
    },
    computed: {
        processedColumns() {
            console.log("columns", this.columns);
            return this.columns.map((column) => {
                const newColumn = {
                    field: column.key || column.field,
                    title: column.title,
                    width: column.width,
                    fixed: column.fixed || undefined,
                    showOverflow:
                        column.showOverflow === false ? false : "tooltip",
                    vxeFormatter: null,
                    editRender: null,
                    slots: column.slots,
                    render: column.render,
                    child: column.child
                };

                if (column.formatNumber) {
                    newColumn.vxeFormatter = ({ cellValue }) => {
                        return typeof number2ThousandsFormat === "function"
                            ? number2ThousandsFormat(cellValue)
                            : cellValue;
                    };
                }
                else if (typeof column.formatter === "function") {
                    newColumn.vxeFormatter = ({
                        cellValue,
                        row,
                        rowIndex,
                        column: vxeCol
                    }) => {
                        return column.formatter(row);
                    };
                }

                if (column.editable) {
                    const editType = column.editType || "text";
                    let componentName = "input";

                    switch (editType) {
                        case "number":
                            componentName = "input";
                            break;
                        case "date":
                            componentName = "input";
                            break;
                        case "select":
                            componentName = "select";
                            break;
                        default:
                            componentName = "input";
                    }

                    newColumn.editRender = {
                        name: componentName,
                        enabled: true,
                        props: {
                            ...(editType === "number"
                                ? { type: "number" }
                                : {}),
                            ...(editType === "date"
                                ? { type: "date", transfer: true }
                                : {}),
                            ...(column.editProps || {})
                        },
                        events: {
                            change: ({ row, column: vxeCol }) => {
                                this.$emit("edit-change", {
                                    row,
                                    field: vxeCol.field
                                });
                            }
                        }
                    };
                }
                return newColumn;
            });
        },
        computedTableHeight() {
            if (this.autoHeight) {
                return "auto";
            }
            return this.tableHeight;
        },
        paginationConfig() {
            return {
                currentPage: this.pagination.currentPage || 1,
                pageSize:
                    this.pagination.pageSize || DEFAULT_PAGE_SIZE_OPTIONS[0],
                total: this.pagination.total || 0
            };
        }
    },
    watch: {
        autoHeight() {
            this.calculateTableHeight();
        },
        extHeight() {
            this.calculateTableHeight();
        }
    },
    mounted() {
        this.calculateTableHeight();
        window.addEventListener("resize", this.calculateTableHeight);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.calculateTableHeight);
    },
    methods: {
        calculateTableHeight() {
            if (this.autoHeight) {
                this.tableHeight = "auto";
                return;
            }
            this.$nextTick(() => {
                const tableEl = this.$refs.vxeTable && this.$refs.vxeTable.$el;
                if (tableEl && tableEl.offsetTop) {
                    const bottomOffset = this.showPage ? 240 : 200;
                    const calculatedHeight
                        = window.innerHeight
                        - tableEl.offsetTop
                        - bottomOffset
                        - this.extHeight;
                    this.tableHeight = Math.max(calculatedHeight, 150);
                    console.log(
                        `Calculated table height: ${this.tableHeight}px (offsetTop: ${tableEl.offsetTop}, extHeight: ${this.extHeight})`
                    );
                }
                else {
                    this.tableHeight = 400;
                }
                if (this.$refs.vxeTable) {
                    this.$refs.vxeTable.recalculate(true);
                }
            });
        },

        handlePageChange({ currentPage, pageSize }) {
            this.$emit("update:pagination", {
                ...this.pagination,
                currentPage,
                pageSize
            });
            this.$emit("page-change", { currentPage, pageSize });
        },

        handleCheckboxChange({ records }) {
            this.selectedRows = records;
            this.$emit("selection-change", this.selectedRows);
        },
        handleCheckboxAllChange({ records }) {
            this.selectedRows = records;
            this.$emit("selection-change", this.selectedRows);
        },

        handleCellDblClick({ row, column }) {
            this.$emit("row-dblclick", row, column.field);
        },

        getVxeTableInstance() {
            return this.$refs.vxeTable;
        },
        validateTableData(rows) {
            const table = this.getVxeTableInstance();
            if (table) {
                return table.validate(rows || true);
            }
            return Promise.resolve(true);
        },

        getRecordset() {
            const table = this.getVxeTableInstance();
            return table
                ? table.getRecordset()
                : {
                    insertRecords: [],
                    removeRecords: [],
                    updateRecords: []
                };
        },

        getUpdateRecords() {
            const table = this.getVxeTableInstance();
            return table ? table.getUpdateRecords() : [];
        },

        clearSelection() {
            this.selectedRows = [];
            const vxeTable = this.getVxeTableInstance();
            if (vxeTable) {
                vxeTable.clearCheckboxRow();
            }
            this.$emit("selection-change", this.selectedRows);
        },

        setSelection(rows, checked) {
            const vxeTable = this.getVxeTableInstance();
            if (vxeTable) {
                vxeTable.setCheckboxRow(rows, checked);
                this.selectedRows = vxeTable.getCheckboxRecords() || [];
            }
            else {
                this.selectedRows = [];
            }
            this.$emit("selection-change", this.selectedRows);
        }
    }
};
</script>

<style scoped>
.stand-pagination-table-container {
    height: 100%;
    display: flex;
    flex-direction: column;

    .vxe-table {
        flex-grow: 1;
    }

    .vxe-pager {
        flex-shrink: 0;
        padding-top: 8px;
    }
}
</style>
